import React from 'react';
import { ArrowLeft, Download, Calendar, RefreshCw, Users, GraduationCap, Clock } from 'lucide-react';
import { useEmailOpens } from '../hooks/useEmailOpens';
import { useAcmBase } from '../hooks/useAcmBase';
import { formatNumber } from '../utils/formatters';
import { useSidebarState } from '../hooks/useSidebarState';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

export function OpenRateReport() {
  const { stats, loading: emailLoading, error: emailError, refresh: refreshEmail } = useEmailOpens({
    realtimeUpdates: true
  });

  const { 
    totalCount, 
    activeCount, 
    loyalCount,
    newStudentsCount,
    loading: studentsLoading,
    error: studentsError,
    refresh: refreshStudents
  } = useAcmBase();

  const { isCollapsed } = useSidebarState();

  const handleRefresh = () => {
    refreshEmail();
    refreshStudents();
  };

  // Dados simulados para o gráfico de área
  const areaData = React.useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => ({
      name: `${i + 1}`,
      taxa: Math.floor(Math.random() * 100),
      baseline: 50
    }));
  }, []);

  if (emailError || studentsError) {
    return <div className="text-red-500">Error: {emailError || studentsError}</div>;
  }

  const loading = emailLoading || studentsLoading;

  return (
    <div className="min-h-screen bg-background text-white p-8">
      <div className={`mx-auto transition-all duration-300 ${
        isCollapsed 
          ? 'max-w-[1400px] px-4' 
          : 'max-w-[1200px]'
      }`}>
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline"
              size="icon"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Relatório de Taxa de Abertura</h1>
              <p className="text-muted-foreground text-sm">Análise detalhada do engajamento por e-mail</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button>
              <Download className="w-4 h-4 mr-2" />
              Exportar relatório
            </Button>
            <Select defaultValue="12">
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Selecione o período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="12">Últimos 12 meses</SelectItem>
                <SelectItem value="6">Últimos 6 meses</SelectItem>
                <SelectItem value="3">Últimos 3 meses</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Student Stats */}
        <div className="grid grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-base">Total de Alunos</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : formatNumber(totalCount)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                +15.2% em relação ao período anterior
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-base">Alunos Ativos</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : formatNumber(activeCount)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                +8.7% em relação ao período anterior
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <GraduationCap className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-base">Alunos Fiéis</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : formatNumber(loyalCount)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                +12.4% em relação ao período anterior
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-base">Novos Alunos</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : formatNumber(newStudentsCount)}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                +20.1% em relação ao período anterior
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Email Stats */}
        <div className="grid grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Total de E-mails</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : formatNumber(stats.totalEmails)}
              </p>
              <p className="text-sm text-emerald-400 mt-1">
                +15.2% em relação ao período anterior
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Taxa de Cliques</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : `${stats.clickThroughRate.toFixed(1)}%`}
              </p>
              <p className="text-sm text-emerald-400 mt-1">
                +8.7% em relação ao período anterior
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Taxa de Abertura</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">
                {loading ? "..." : `${stats.openRate.toFixed(1)}%`}
              </p>
              <p className="text-sm text-emerald-400 mt-1">
                +32.1% em relação ao período anterior
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Area Chart */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Evolução da Taxa de Abertura</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={areaData}>
                  <defs>
                    <linearGradient id="colorTaxa" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2C2D3C" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#9CA3AF"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#9CA3AF"
                    fontSize={12}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1C1D2C',
                      border: '1px solid #2C2D3C',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#fff' }}
                    itemStyle={{ color: '#9CA3AF' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="taxa"
                    stroke="#6366f1"
                    fillOpacity={1}
                    fill="url(#colorTaxa)"
                  />
                  <Area
                    type="monotone"
                    dataKey="baseline"
                    stroke="#2C2D3C"
                    strokeDasharray="3 3"
                    fill="none"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Additional Stats */}
        <div className="grid grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Estatísticas de E-mail</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total de e-mails abertos</span>
                  <span className="text-xl font-bold">
                    {loading ? "..." : formatNumber(stats.totalEmails)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Taxa de cliques</span>
                  <span className="text-xl font-bold">
                    {loading ? "..." : `${stats.clickThroughRate.toFixed(1)}%`}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Métricas de Engajamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Taxa de abertura</span>
                  <span className="text-xl font-bold">
                    {loading ? "..." : `${stats.openRate.toFixed(1)}%`}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Média de engajamento</span>
                  <span className="text-xl font-bold">
                    {loading ? "..." : `${((stats.clickThroughRate + stats.openRate) / 2).toFixed(1)}%`}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}